import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';
import { tr } from '../til';

const karta = {
  background: '#fff',
  borderRadius: '12px',
  padding: '20px',
  boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
  marginBottom: '16px'
};

const btn = {
  padding: '8px 16px',
  border: 'none',
  borderRadius: '8px',
  fontSize: '13px',
  cursor: 'pointer',
  color: '#fff'
};

const smallBtn = {
  padding: '6px 12px',
  background: '#eef1f7',
  color: '#1a1a2e',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '12px',
  cursor: 'pointer'
};

const vaqtMatni = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit' });
};

// Signal kelganidan beri qancha vaqt o'tgani — rahbar uchun eng muhim raqam.
const qanchaOtdi = (iso) => {
  const soniya = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (soniya < 60) return tr('{n} soniya oldin', { n: soniya });
  const daqiqa = Math.floor(soniya / 60);
  if (daqiqa < 60) return tr('{n} daqiqa oldin', { n: daqiqa });
  const soat = Math.floor(daqiqa / 60);
  if (soat < 24) return tr('{n} soat oldin', { n: soat });
  return tr('{n} kun oldin', { n: Math.floor(soat / 24) });
};

// Ovoz fayl sifatida qo'shilmadi: ilova assetiga bog'lanib qolmasin va
// yuklanmay qolgan fayl tufayli signal jimgina o'tib ketmasin. Brauzerning
// o'zi chiqaradigan ikki qisqa signal — hech qanday tashqi resurs kerak emas.
const ovozChalish = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    [0, 0.35].forEach((kechikish) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = 880;
      gain.gain.value = 0.15;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + kechikish);
      osc.stop(ctx.currentTime + kechikish + 0.25);
    });
    setTimeout(() => ctx.close(), 1500);
  } catch {
    // Ovoz chiqmasa ham signal ekranda ko'rinadi — to'xtatib qo'ymaymiz.
  }
};

function Sos() {
  const [signallar, setSignallar] = useState([]);
  const [faqatOchiq, setFaqatOchiq] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [yangiBor, setYangiBor] = useState(false);

  const token = localStorage.getItem('token');

  // Qaysi signallarni allaqachon ko'rganimiz. Ref, chunki o'zgarishi
  // qayta render talab qilmaydi va poll ichida eng so'nggi qiymat kerak.
  const korilganRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const url = faqatOchiq ? `${API}/sos?status=ochiq` : `${API}/sos`;
      const res = await axios.get(url, { headers: { authorization: token } });
      setSignallar(res.data);
      setError('');

      const ochiqIdlar = res.data.filter((s) => s.status === 'ochiq').map((s) => s.id);

      // Birinchi yuklanishda ovoz chalinmaydi — sahifa ochilishining o'zi
      // "yangi signal keldi" degani emas.
      if (korilganRef.current !== null) {
        const yangi = ochiqIdlar.filter((id) => !korilganRef.current.has(id));
        if (yangi.length > 0) {
          ovozChalish();
          setYangiBor(true);
        }
      }
      korilganRef.current = new Set(ochiqIdlar);
    } catch (err) {
      setError(err.response?.data?.error || tr("Signallarni olishda xato"));
    }
  }, [token, faqatOchiq]);

  useEffect(() => { load(); }, [load]);

  // Panel ochiq turganda yangi signal o'zidan ko'rinsin — rahbar sahifani
  // qo'lda yangilab o'tirmaydi.
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  // Boshqa ilovada ishlayotgan rahbar ham sezsin.
  useEffect(() => {
    const ochiqSoni = signallar.filter((s) => s.status === 'ochiq').length;
    document.title = ochiqSoni > 0 ? `🆘 (${ochiqSoni}) SOS — Manguberdi` : 'Manguberdi admin';
    return () => { document.title = 'Manguberdi admin'; };
  }, [signallar]);

  const qabul = async (sos, nom) => {
    setBusy(`${sos.id}-${nom}`);
    setError('');
    try {
      await axios.post(`${API}/sos/${sos.id}/qabul`, { nom },
        { headers: { authorization: token } });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || tr("Qabul qilinmadi."));
    } finally {
      setBusy(null);
    }
  };

  const yopish = async (sos) => {
    if (!window.confirm(tr("Signal yopilsinmi? Xodimga yordam yetganiga ishonch hosil qiling."))) return;
    setBusy(`${sos.id}-yopish`);
    setError('');
    try {
      await axios.post(`${API}/sos/${sos.id}/yopish`, {},
        { headers: { authorization: token } });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || tr("Yopilmadi."));
    } finally {
      setBusy(null);
    }
  };

  const ochiqlar = signallar.filter((s) => s.status === 'ochiq');
  const yopilganlar = signallar.filter((s) => s.status !== 'ochiq');

  const holatBelgisi = (h) => {
    if (h.holat === 'qabul') return { matn: tr("✅ Qabul qildi"), rang: '#1a7f37' };
    if (h.holat === 'yolda') return { matn: tr("🚗 Yo'lda"), rang: '#0969da' };
    if (h.holat === 'yopildi') return { matn: tr("⏹️ Yopildi"), rang: '#57606a' };
    return { matn: tr("⏳ Kutilmoqda"), rang: '#9a6700' };
  };

  const qator = (s) => {
    const ochiq = s.status === 'ochiq';
    return (
      <div
        key={s.id}
        style={{
          ...karta,
          border: ochiq ? '2px solid #d1242f' : '1px solid #eee',
          background: ochiq ? '#fff5f5' : '#fff'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '17px', fontWeight: 600, color: ochiq ? '#d1242f' : '#1a1a2e' }}>
              {ochiq && '🆘 '}{s.full_name}
              {s.rank && <span style={{ fontSize: '13px', color: '#666', fontWeight: 400 }}> · {s.rank}</span>}
            </div>
            <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
              {tr("Xizmat raqami:")} <b>{s.badge_number || '—'}</b>
              {' · '}{s.district_name || tr("tuman ko'rsatilmagan")}
            </div>
            <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
              {vaqtMatni(s.created_at)} · <b>{qanchaOtdi(s.created_at)}</b>
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            {s.gps_available ? (
              <>
                <a
                  href={`https://www.google.com/maps?q=${s.latitude},${s.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ ...btn, background: '#0969da', textDecoration: 'none', display: 'inline-block' }}
                >
                  📍 {tr("Xaritada ochish")}
                </a>
                <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                  {Number(s.latitude).toFixed(6)}, {Number(s.longitude).toFixed(6)}
                  {s.accuracy_meters != null && ` (±${Math.round(s.accuracy_meters)} m)`}
                </div>
              </>
            ) : (
              // Joylashuvsiz signal ham to'liq huquqli signal — GPS binoda
              // yoki yerto'lada yo'q bo'ladi va yordam o'shanda ko'proq kerak.
              <div style={{ fontSize: '13px', color: '#9a6700', fontWeight: 600 }}>
                ⚠️ {tr("Joylashuv aniqlanmadi")}
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
          {(s.holatlar || []).map((h) => {
            const b = holatBelgisi(h);
            return (
              <div key={h.nom} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '6px 0', flexWrap: 'wrap'
              }}>
                <span style={{ minWidth: '90px', fontSize: '13px', fontWeight: 600 }}>{h.nom}</span>
                <span style={{ color: b.rang, fontSize: '13px', minWidth: '130px' }}>{b.matn}</span>
                {h.kim && <span style={{ fontSize: '12px', color: '#666' }}>{h.kim}</span>}
                {h.vaqt && <span style={{ fontSize: '12px', color: '#888' }}>{vaqtMatni(h.vaqt)}</span>}
                {ochiq && h.holat === 'kutilmoqda' && (
                  <button
                    style={{ ...btn, background: '#1a7f37' }}
                    disabled={busy === `${s.id}-${h.nom}`}
                    onClick={() => qabul(s, h.nom)}
                  >
                    {busy === `${s.id}-${h.nom}` ? '...' : tr("{nom} — qabul qildim", { nom: h.nom })}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {ochiq && (
          <div style={{ marginTop: '12px' }}>
            <button
              style={smallBtn}
              disabled={busy === `${s.id}-yopish`}
              onClick={() => yopish(s)}
            >
              {busy === `${s.id}-yopish` ? '...' : tr("⏹️ Signalni yopish")}
            </button>
          </div>
        )}

        {!ochiq && s.closed_at && (
          <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
            {tr("Yopilgan: {vaqtMatni}", { vaqtMatni: vaqtMatni(s.closed_at) })}
            {s.closed_by_name && ` · ${s.closed_by_name}`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0 }}>🆘 {tr("SOS signallari")}</h2>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: '13px' }}>
            {tr("Ro'yxat har 10 soniyada o'zidan yangilanadi")}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {yangiBor && (
            <button
              style={{ ...btn, background: '#d1242f' }}
              onClick={() => setYangiBor(false)}
            >
              🔔 {tr("Yangi signal keldi — bosib o'chiring")}
            </button>
          )}
          <button style={smallBtn} onClick={() => setFaqatOchiq(!faqatOchiq)}>
            {faqatOchiq ? tr("Hammasini ko'rsatish") : tr("Faqat ochiqlari")}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ ...karta, background: '#ffebe9', color: '#d1242f', padding: '12px 16px' }}>
          {error}
        </div>
      )}

      {ochiqlar.length === 0 && yopilganlar.length === 0 && (
        <div style={{ ...karta, textAlign: 'center', color: '#666', padding: '40px' }}>
          {tr("Signal yo'q.")}
        </div>
      )}

      {ochiqlar.length > 0 && (
        <>
          <h3 style={{ color: '#d1242f', fontSize: '15px', marginBottom: '10px' }}>
            {tr("Ochiq signallar ({soni})", { soni: ochiqlar.length })}
          </h3>
          {ochiqlar.map(qator)}
        </>
      )}

      {yopilganlar.length > 0 && (
        <>
          <h3 style={{ color: '#57606a', fontSize: '15px', margin: '24px 0 10px' }}>
            {tr("Yopilganlar ({soni})", { soni: yopilganlar.length })}
          </h3>
          {yopilganlar.map(qator)}
        </>
      )}
    </div>
  );
}

export default Sos;
